import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiClient } from './client';
import { monthlyKeys } from './plan-matriz-monthly';
import { closureKeys } from './closure';
import type {
  TicketFilter,
  CreateTicketInput,
  UpdateTicketInput,
  TransitionBody,
  EstadoOt,
  PaginationQuery,
  CreateTooltimeInput,
  UpdateTooltimeInput,
  NoEjecutadoBody,
  EstadoActividadValue,
  UpdateActividadEstado,
  AdjuntoPresign,
  AdjuntoConfirm,
  AdjuntoWithUrl,
  CloseValidationResult,
  VariableValor,
} from '@sime/shared';

// --- Response Types ---

interface TicketListItem {
  id: number;
  correlativo: string;
  estadoOt: EstadoOt;
  periodo: string;
  fechaProgramada: string | null;
  observaciones: string | null;
  observacionSupervisor: string | null;
  prioridad: number | null;
  // NPL activity name (ot.servicio). For NPL tickets there is no planMatriz, so
  // the activity name lives here; "Nombre de la Actividad" = planMatriz?.nombre ?? servicio.
  servicio: string | null;
  // Plan Matriz activity name — the primary source for the ticket's display name
  // in the list (falls back to `servicio` when null). Selected by TICKET_INCLUDE.
  planMatriz?: { nombre: string } | null;
  createdAt: string;
  updatedAt: string;
  empresa?: { id: number; nombre: string };
  subcontrato?: { id: number; nombre: string };
  ambito?: { id: number; nombre: string };
  ubicacion?: { id: number; nombre: string };
  tipoOt?: { id: number; nombre: string };
  tipoMantenimiento?: { id: number; nombre: string };
  operador?: { id: number; nombre: string };
  encargado?: { id: number; nombre: string };
}

interface CoordinatorDecision {
  decision: 'APROBADO' | 'RECHAZADO' | null;
  observacion: string | null;
  decidedAt: string | null;
  decider: { id: number; nombre: string } | null;
  lote: { id: number; uuid: string; periodo: string };
}

interface TicketDetail extends TicketListItem {
  empresaId: number;
  subcontratoId: number;
  ambitoId: number;
  ubicacionId: number;
  tipoOtId: number;
  tipoMantenimientoId: number;
  pautasId: number;
  // Legacy stored shift id — null on tickets created after turno became
  // date-derived. Do NOT read this for the shift; use `turno.nombre`.
  turnosId: number | null;
  planMatrizId: number | null;
  createdBy: number | null;
  updatedBy: number | null;
  pauta?: { id: number; nombre: string };
  /** Datos del equipo de la OT (marca, capacidad…): campos con su valor. Ausente/null = sin datos. */
  datosEquipo?: VariableValor[] | null;
  // Backend relation is `turno` (singular) — now the date-derived shift, with
  // `nombre` authoritative. (The old `turnos` alias was removed; it was never
  // populated by the backend and caused a silent always-undefined read.)
  turno?: { id: number; nombre: string };
  planMatriz?: {
    id: number;
    nombre: string;
    catalogoeco?: { id: number; item: string; eco: string; unidad: string | null } | null;
    tipoEquipo?: { id: number; detalle: string } | null;
  };
  usuario?: { id: number; nombre: string };
  lotesAprobacion?: CoordinatorDecision[];
}

interface HistoryEntry {
  id: number;
  ticketId: number;
  fromState: EstadoOt | null;
  toState: EstadoOt;
  reason: string | null;
  userId: number;
  user?: { id: number; nombre: string };
  createdAt: string;
}

interface PaginatedTicketsResponse {
  success: true;
  data: TicketListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SingleTicketResponse {
  success: true;
  data: TicketDetail;
}

interface TicketHistoryResponse {
  success: true;
  data: HistoryEntry[];
}

interface TransitionResponse {
  success: true;
  data: { success: true; fromState: EstadoOt; toState: EstadoOt };
}

// --- Query Keys ---

export const ticketKeys = {
  all: ['tickets'] as const,
  lists: () => [...ticketKeys.all, 'list'] as const,
  list: (filter: TicketFilter, pagination: Partial<PaginationQuery>) =>
    [...ticketKeys.lists(), filter, pagination] as const,
  details: () => [...ticketKeys.all, 'detail'] as const,
  detail: (id: number) => [...ticketKeys.details(), id] as const,
  histories: () => [...ticketKeys.all, 'history'] as const,
  history: (id: number) => [...ticketKeys.histories(), id] as const,
  pdfs: () => [...ticketKeys.all, 'pdf'] as const,
  pdf: (id: number) => [...ticketKeys.pdfs(), id] as const,
  verifications: () => [...ticketKeys.all, 'verification'] as const,
  verification: (id: number) => [...ticketKeys.verifications(), id] as const,
};

// --- Query Hooks ---

export function useTickets(
  filter: TicketFilter = {},
  pagination: Partial<PaginationQuery> = {},
  options?: Omit<UseQueryOptions<PaginatedTicketsResponse>, 'queryKey' | 'queryFn'>,
) {
  const params = new URLSearchParams();
  // Multi-estado (CSV) takes precedence over the singular filter — mirrors the
  // backend's precedence in listTickets().
  if (filter.estadoOtIn && filter.estadoOtIn.length > 0) {
    params.set('estadoOtIn', filter.estadoOtIn.join(','));
  } else if (filter.estadoOt) {
    params.set('estadoOt', filter.estadoOt);
  }
  if (filter.ambitoId) params.set('ambitoId', String(filter.ambitoId));
  if (filter.periodo) params.set('periodo', filter.periodo);
  if (filter.periodoDesde) params.set('periodoDesde', filter.periodoDesde);
  if (filter.periodoHasta) params.set('periodoHasta', filter.periodoHasta);
  if (filter.supervisorId) params.set('supervisorId', String(filter.supervisorId));
  if (filter.empresaId) params.set('empresaId', String(filter.empresaId));
  if (filter.tipoEquipoId) params.set('tipoEquipoId', String(filter.tipoEquipoId));
  if (filter.catalogoecoId) params.set('catalogoecoId', String(filter.catalogoecoId));
  if (filter.search) params.set('search', filter.search);
  if (pagination.page) params.set('page', String(pagination.page));
  if (pagination.limit) params.set('limit', String(pagination.limit));
  if (pagination.sortBy) params.set('sortBy', pagination.sortBy);
  if (pagination.sortOrder) params.set('sortOrder', pagination.sortOrder);

  const qs = params.toString();
  const endpoint = `/tickets${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: ticketKeys.list(filter, pagination),
    queryFn: () => apiClient<PaginatedTicketsResponse>(endpoint),
    ...options,
  });
}

export function useTicket(id: number, options?: Omit<UseQueryOptions<SingleTicketResponse>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: () => apiClient<SingleTicketResponse>(`/tickets/${id}`),
    enabled: id > 0,
    ...options,
  });
}

export function useTicketHistory(id: number, options?: Omit<UseQueryOptions<TicketHistoryResponse>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: ticketKeys.history(id),
    queryFn: () => apiClient<TicketHistoryResponse>(`/tickets/${id}/history`),
    enabled: id > 0,
    ...options,
  });
}

// --- PDF & Verification Types ---

export interface TicketPdfData {
  url: string;
  isSigned: boolean;
  isLegacy: boolean;
  hasPdf: boolean;
}

export interface VerificationSigner {
  id: number;
  nombre: string;
}

export interface TicketVerificationData {
  id: number;
  hashPdf: string;
  hashPdfFirmado: string | null;
  isSigned: boolean;
  signedAt: string | null;
  signedBy: VerificationSigner | null;
  ipAddress: string | null;
  deviceFingerprint: string | null;
  createdAt: string;
}

interface TicketPdfResponse {
  success: true;
  data: TicketPdfData;
}

interface TicketVerificationResponse {
  success: true;
  data: TicketVerificationData | null;
}

// --- PDF & Verification Hooks ---

export function useTicketPdf(id: number, options?: Omit<UseQueryOptions<TicketPdfResponse>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: ticketKeys.pdf(id),
    queryFn: () => apiClient<TicketPdfResponse>(`/tickets/${id}/pdf`),
    enabled: id > 0,
    // Presigned URLs expire after 1 hour; refetch every 50 minutes to stay fresh
    staleTime: 50 * 60 * 1000,
    // The PDF's firmado state can change in ANOTHER browser (the coordinator
    // signs from the miniapp), so the supervisor's client can't be invalidated
    // by a local mutation. Always refetch on mount so reopening the ticket shows
    // the real signed state instead of a stale "Sin firmar".
    refetchOnMount: 'always',
    ...options,
  });
}

/**
 * Trigger a real file download of a ticket PDF (Save dialog).
 *
 * The `/pdf` endpoint normally returns an *inline* presigned S3 URL, meant for
 * viewing (iframe / new tab). Linking an `<a>` straight at it never downloads:
 * the `download` attribute is ignored for cross-origin URLs, and the `inline`
 * disposition makes the browser render the PDF in a tab. `?download=1` returns a
 * presigned URL with `Content-Disposition: attachment`, so S3 itself serves it
 * as a download — the reliable cross-origin pattern.
 *
 * Throws if the ticket has no PDF; the caller surfaces the message.
 */
export async function downloadTicketPdf(id: number): Promise<void> {
  const res = await apiClient<TicketPdfResponse>(`/tickets/${id}/pdf?download=1`);
  if (!res.data.hasPdf || !res.data.url) {
    throw new Error('PDF no disponible para este ticket.');
  }
  const a = document.createElement('a');
  a.href = res.data.url;
  a.rel = 'noopener';
  // The filename is set by S3 via Content-Disposition; no `download` attr needed.
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function useTicketVerification(id: number, options?: Omit<UseQueryOptions<TicketVerificationResponse>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: ticketKeys.verification(id),
    queryFn: () => apiClient<TicketVerificationResponse>(`/tickets/${id}/verification`),
    enabled: id > 0,
    // Signing happens cross-actor (coordinator's browser); always refetch on
    // mount and treat as immediately stale so the Certificación/firma state is
    // never served stale to the supervisor.
    staleTime: 0,
    refetchOnMount: 'always',
    ...options,
  });
}

// --- Mutation Hooks ---

export function useGeneratePdf(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ success: true; data: { s3Key: string; hash: string; verificacionId: number } }>(
        '/signatures/generate-pdf',
        { method: 'POST', body: JSON.stringify({ ticketId }) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.pdf(ticketId) });
      // Regenerating the PDF can change the firma/verification state too.
      void queryClient.invalidateQueries({ queryKey: ticketKeys.verification(ticketId) });
    },
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTicketInput) =>
      apiClient<SingleTicketResponse>('/tickets', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
    },
  });
}

export function useUpdateTicket(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateTicketInput) =>
      apiClient<SingleTicketResponse>(`/tickets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      // Calendar views (monthly/weekly/daily) live under a separate query
      // namespace (`monthlyKeys.all`). PUT /tickets/:id may change estadoOt
      // (e.g. mantenedor edits a PROGRAMADO/RECHAZADO ticket) and without
      // this the calendar card keeps the previous color/badge until staleTime.
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
      // Closure Mensual dashboard counts are keyed under closureKeys and were
      // never invalidated by ticket transitions → the dashboard showed stale
      // counts until a 30s poll or reload ("no me aparece hasta que recargué").
      void queryClient.invalidateQueries({ queryKey: closureKeys.all });
    },
  });
}

export function useTransitionTicket(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TransitionBody) =>
      apiClient<TransitionResponse>(`/tickets/${id}/transition`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.history(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      // Mantenedor list lives under a separate query namespace
      // (mantenedorKeys, root ['mantenedor']). Without this, a transition
      // driven from the mantenedor's Reabrir flow succeeds in DB but the
      // row keeps showing the stale state until the user refreshes.
      void queryClient.invalidateQueries({ queryKey: ['mantenedor'] });
      // Calendar views (monthly/weekly/daily) read estadoOt for color/badge.
      // Without this, after EJECUTADO→APROBADO the card keeps showing as
      // PROGRAMADO/EJECUTADO in the calendar grid until staleTime expires.
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
      // Closure Mensual dashboard counts are keyed under closureKeys and were
      // never invalidated by ticket transitions → the dashboard showed stale
      // counts until a 30s poll or reload ("no me aparece hasta que recargué").
      void queryClient.invalidateQueries({ queryKey: closureKeys.all });
    },
  });
}

// --- Execution API Types ---

export interface Subactividad {
  id: number;
  descripcion: string;
  completado: boolean;
  orden: number;
}

export interface Actividad {
  id: number;
  descripcion: string;
  completado: boolean;
  estadoActividad: EstadoActividadValue | null;
  observaciones: string | null;
  orden: number;
  /** Sección del checklist (null/ausente = sin sección; cachés viejos no la traen). */
  seccion?: string | null;
  /** Mediciones del ítem con su valor (ausente/[] = sin mediciones). */
  variables?: VariableValor[];
  subactividades: Subactividad[];
  _count?: { fotos: number };
}

export interface EstadoActividadOption {
  value: EstadoActividadValue;
  label: string;
  descripcion: string;
}

export interface ChecklistResponse {
  success: true;
  data: Actividad[];
}

export interface TooltimeEntry {
  id: number;
  otId: number;
  inicio: string;
  termino: string;
  descripcion: string | null;
  trabajadoresCount: number;
  tipoActividad: string | null;
  turno: string | null;
  area: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TooltimeListResponse {
  success: true;
  data: TooltimeEntry[];
}

interface TooltimeResponse {
  success: true;
  data: TooltimeEntry;
}

export interface CncCode {
  id: number;
  codigo: string;
  descripcion: string;
}

interface CncCodesResponse {
  success: true;
  data: CncCode[];
}

// --- Execution Query Keys ---

export const executionKeys = {
  checklist: (ticketId: number) => ['tickets', ticketId, 'checklist'] as const,
  tooltime: (ticketId: number) => ['tickets', ticketId, 'tooltime'] as const,
  cncCodes: () => ['cnc-codes'] as const,
};

// --- Execution Transition Hooks ---

export function useStartTicket(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<TransitionResponse>(`/tickets/${id}/start`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.history(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: ['mantenedor'] });
      // PROGRAMADO→INICIADO must repaint the calendar card (border, dot,
      // draggable, lose reschedule affordance once it's no longer PROGRAMADO).
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
      // Closure Mensual dashboard counts are keyed under closureKeys and were
      // never invalidated by ticket transitions → the dashboard showed stale
      // counts until a 30s poll or reload ("no me aparece hasta que recargué").
      void queryClient.invalidateQueries({ queryKey: closureKeys.all });
      // Líder "Mis OTs" filters estadoOt IN (PROGRAMADO, INICIADO) with a
      // 5-min staleTime and no poll: without this, transitioning a ticket and
      // navigating back to /l kept showing the stale card (literal key to
      // avoid importing lider.ts here).
      void queryClient.invalidateQueries({ queryKey: ['lider'] });
    },
  });
}

export function useCompleteTicket(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<TransitionResponse>(`/tickets/${id}/complete`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.history(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: ['mantenedor'] });
      // INICIADO→EJECUTADO must repaint the calendar card (color → green,
      // PDF icon appears via STATES_WITH_PDF, no longer draggable).
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
      // Closure Mensual dashboard counts are keyed under closureKeys and were
      // never invalidated by ticket transitions → the dashboard showed stale
      // counts until a 30s poll or reload ("no me aparece hasta que recargué").
      void queryClient.invalidateQueries({ queryKey: closureKeys.all });
      // Líder "Mis OTs" must drop the completed ticket card (see useStartTicket).
      void queryClient.invalidateQueries({ queryKey: ['lider'] });
    },
  });
}

export function useNoEjecutado(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NoEjecutadoBody) =>
      apiClient<TransitionResponse>(`/tickets/${id}/no-ejecutado`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.history(id) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: ['mantenedor'] });
      // *→NO_EJECUTADO must repaint the calendar card (color → neutral grey,
      // loses draggable/PDF affordances).
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
      // Closure Mensual dashboard counts are keyed under closureKeys and were
      // never invalidated by ticket transitions → the dashboard showed stale
      // counts until a 30s poll or reload ("no me aparece hasta que recargué").
      void queryClient.invalidateQueries({ queryKey: closureKeys.all });
      // Líder "Mis OTs" must drop the killed ticket card — otherwise after
      // "No Ejecutado" the líder lands on /l and the dead ticket still shows
      // as active for up to 5 min (silent-failure-hunter HIGH, 2026-06-11).
      void queryClient.invalidateQueries({ queryKey: ['lider'] });
    },
  });
}

// --- Checklist Hooks ---

export function useChecklist(ticketId: number) {
  return useQuery({
    queryKey: executionKeys.checklist(ticketId),
    queryFn: () => apiClient<ChecklistResponse>(`/tickets/${ticketId}/checklist`),
    enabled: ticketId > 0,
  });
}

export function useToggleActividad(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ actividadId, completado }: { actividadId: number; completado: boolean }) =>
      apiClient<{ success: true }>(`/tickets/${ticketId}/checklist/actividad/${actividadId}`, {
        method: 'PATCH',
        body: JSON.stringify({ completado }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: executionKeys.checklist(ticketId) });
    },
  });
}

export function useUpdateActividadEstado(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ actividadId, data }: { actividadId: number; data: UpdateActividadEstado }) =>
      apiClient<{ success: true; data: Actividad }>(`/tickets/${ticketId}/checklist/actividad/${actividadId}/estado`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: executionKeys.checklist(ticketId) });
    },
  });
}

export function useToggleSubactividad(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subactividadId, completado }: { subactividadId: number; completado: boolean }) =>
      apiClient<{ success: true }>(`/tickets/${ticketId}/checklist/subactividad/${subactividadId}`, {
        method: 'PATCH',
        body: JSON.stringify({ completado }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: executionKeys.checklist(ticketId) });
    },
  });
}

export function useBulkChecklist(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: Array<{ id: number; completado: boolean }>) =>
      apiClient<{ success: true }>(`/tickets/${ticketId}/checklist/bulk`, {
        method: 'PATCH',
        body: JSON.stringify({ items }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: executionKeys.checklist(ticketId) });
    },
  });
}

// --- Tooltime Hooks ---

export function useTooltime(ticketId: number) {
  return useQuery({
    queryKey: executionKeys.tooltime(ticketId),
    queryFn: () => apiClient<TooltimeListResponse>(`/tickets/${ticketId}/tooltime`),
    enabled: ticketId > 0,
  });
}

export function useCreateTooltime(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTooltimeInput) =>
      apiClient<TooltimeResponse>(`/tickets/${ticketId}/tooltime`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: executionKeys.tooltime(ticketId) });
    },
  });
}

export function useUpdateTooltime(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tooltimeId, data }: { tooltimeId: number; data: UpdateTooltimeInput }) =>
      apiClient<TooltimeResponse>(`/tickets/${ticketId}/tooltime/${tooltimeId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: executionKeys.tooltime(ticketId) });
    },
  });
}

export function useDeleteTooltime(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tooltimeId: number) =>
      apiClient<undefined>(`/tickets/${ticketId}/tooltime/${tooltimeId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: executionKeys.tooltime(ticketId) });
    },
  });
}

// --- CNC Codes Hook ---

export function useCncCodes() {
  return useQuery({
    queryKey: executionKeys.cncCodes(),
    queryFn: () => apiClient<CncCodesResponse>('/cnc-codes'),
    staleTime: 10 * 60 * 1000, // CNC codes rarely change
  });
}

// --- Adjuntos (Certificados & Respaldos) ---

interface AdjuntosListResponse {
  success: true;
  data: AdjuntoWithUrl[];
}

interface AdjuntoPresignResponse {
  success: true;
  data: { uploadUrl: string; s3Key: string; fileId: string };
}

interface AdjuntoConfirmResponse {
  success: true;
  data: { id: number };
}

interface CloseValidationResponse {
  success: true;
  data: CloseValidationResult;
}

export const adjuntoKeys = {
  all: (ticketId: number) => ['tickets', ticketId, 'adjuntos'] as const,
  validation: (ticketId: number) => ['tickets', ticketId, 'validate-close'] as const,
};

export function useAdjuntos(ticketId: number) {
  return useQuery({
    queryKey: adjuntoKeys.all(ticketId),
    queryFn: () => apiClient<AdjuntosListResponse>(`/tickets/${ticketId}/adjuntos`),
    enabled: ticketId > 0,
  });
}

export function useAdjuntoPresign(ticketId: number) {
  return useMutation({
    mutationFn: (data: AdjuntoPresign) =>
      apiClient<AdjuntoPresignResponse>(`/tickets/${ticketId}/adjuntos/presign`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export function useAdjuntoConfirm(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AdjuntoConfirm) =>
      apiClient<AdjuntoConfirmResponse>(`/tickets/${ticketId}/adjuntos/confirm`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: async () => {
      // Await so mutateAsync only resolves after the list is fresh — prevents the
      // "upload then immediately delete" race where the UI still shows stale state.
      await queryClient.invalidateQueries({ queryKey: adjuntoKeys.all(ticketId) });
    },
  });
}

export function useDeleteAdjunto(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (adjuntoId: number) =>
      apiClient<undefined>(`/tickets/${ticketId}/adjuntos/${adjuntoId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adjuntoKeys.all(ticketId) });
    },
  });
}

export function useCloseValidation(ticketId: number, options?: Omit<UseQueryOptions<CloseValidationResponse>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: adjuntoKeys.validation(ticketId),
    queryFn: () => apiClient<CloseValidationResponse>(`/tickets/${ticketId}/validate-close`),
    enabled: ticketId > 0,
    staleTime: 0, // Always refetch — validation depends on current ticket state
    ...options,
  });
}

// --- Assign Pauta Hook ---

interface AssignPautaResponse {
  success: true;
  data: { activitiesCreated: number; activitiesDeleted: number };
}

export function useAssignPauta(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { pautaId: number; replace: boolean }) =>
      apiClient<AssignPautaResponse>(`/tickets/${ticketId}/assign-pauta`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      void queryClient.invalidateQueries({ queryKey: executionKeys.checklist(ticketId) });
    },
  });
}

// Re-export types for page components
export type { TicketListItem, TicketDetail, HistoryEntry };
